import React, { lazy, Suspense, useState, useEffect, useCallback, createContext } from 'react'
import Backdrop from '@mui/material/Backdrop'
import CircularProgress from '@mui/material/CircularProgress'
import versionControlAPI from '../api/versionControl'
import { compareSemver } from '../semverCompare'

const UpdateAvailableSheet = lazy(() => import('../../components/update/UpdateAvailableSheet'))

const PwaVersionContext = createContext({ pwaVersion: null })

const PWA_VERSION_STORAGE_KEY = 'appVersion'

const isNativeWebView = () =>
  typeof window !== 'undefined' && typeof window.ReactNativeWebView !== 'undefined'

/**
 * Clear all PWA cache (Cache API + service workers) and remove version key. Returns a Promise.
 * Works in both browser and WebView (same origin storage). We only remove PWA_VERSION_STORAGE_KEY so auth/settings stay.
 */
async function clearPwaCacheAndVersionKey() {
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(PWA_VERSION_STORAGE_KEY)
  } catch (_) {}

  if (typeof caches === 'undefined' || !caches.keys) {
    return Promise.resolve()
  }
  
  return Promise.all([
    caches.keys().then((keys) => Promise.all(keys.map((name) => caches.delete(name).catch(() => {})))),
    typeof navigator !== 'undefined' && navigator.serviceWorker && navigator.serviceWorker.getRegistrations
      ? await navigator.serviceWorker.getRegistrations().then((regs) => Promise.all(regs.map((r) => r.unregister().catch(() => {}))))
      : Promise.resolve(),
  ]).catch(() => {})
}

/**
 * Hard refresh: clear cache + version key, then reload. Used in browser.
 */
function hardRefreshPwa() {
  clearPwaCacheAndVersionKey().then(() => window.location.reload())
}

export function PwaVersionProvider({ children }) {
  const [pwaVersion, setPwaVersion] = useState(null)
  const [showUpdateDialog, setShowUpdateDialog] = useState(false)
  const [updateSheetPayload, setUpdateSheetPayload] = useState(null)

  const fetchAndCheckVersion = useCallback(async () => {
    if (typeof window === 'undefined') return
    try {
      const res = await versionControlAPI.getNativeAppVersion()
      const payload = res?.data?.data || res?.data
      const latestPwa = payload?.minVersionPwa || payload?.minVersion
      if (!latestPwa || typeof latestPwa !== 'string') return

      const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(PWA_VERSION_STORAGE_KEY) : null

      if (!stored || stored.trim() === '') {
        localStorage.setItem(PWA_VERSION_STORAGE_KEY, latestPwa.trim())
        setPwaVersion(latestPwa.trim())
        setUpdateSheetPayload(null)
        return
      }

      setPwaVersion(stored)
      const storedVer = stored.trim()
      const latestVer = latestPwa.trim()
      if (compareSemver(storedVer, latestVer) !== 0) {
        setUpdateSheetPayload({
          minVersion: latestVer,
          latestChange: payload?.latestVersionChange ?? null,
        })
        setShowUpdateDialog(true)
      }
    } catch (err) {
      console.warn('PWA version check failed', err)
      const fallback = typeof localStorage !== 'undefined' ? localStorage.getItem(PWA_VERSION_STORAGE_KEY) : null
      if (fallback) setPwaVersion(fallback)
    }
  }, [])

  useEffect(() => {
    fetchAndCheckVersion()
    // In WebView native may inject version on onLoadEnd; retry once so we see updated DB version
    if (isNativeWebView()) {
      const t = setTimeout(fetchAndCheckVersion, 1000)
      return () => clearTimeout(t)
    }
  }, [fetchAndCheckVersion])

  const handleRefresh = () => {
    setShowUpdateDialog(false)
    if (isNativeWebView() && typeof window.ReactNativeWebView?.postMessage === 'function') {
      clearPwaCacheAndVersionKey().then(() => {
        try {
          window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'PWA_FORCE_REFRESH' }))
        } catch (_) {}
      })
      return
    }
    hardRefreshPwa()
  }

  return (
    <PwaVersionContext.Provider value={{ pwaVersion, refetchVersion: fetchAndCheckVersion }}>
      {children}
      {showUpdateDialog && (
        <Suspense
          fallback={(
            <Backdrop open sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}>
              <CircularProgress color="inherit" />
            </Backdrop>
          )}
        >
          <UpdateAvailableSheet
            open={showUpdateDialog}
            onClose={() => setShowUpdateDialog(false)}
            onPrimary={handleRefresh}
            primaryLabel="Refresh now"
            variant="pwa"
            minVersion={updateSheetPayload?.minVersion || ''}
            latestChange={updateSheetPayload?.latestChange ?? null}
          />
        </Suspense>
      )}
    </PwaVersionContext.Provider>
  )
}

export function usePwaVersion() {
  const ctx = React.useContext(PwaVersionContext)
  return ctx?.pwaVersion ?? null
}

export default PwaVersionContext
